import axios, { Axios, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import {TeacherInfo, TeacherCheckInParams, Child } from '../types';
// Import packageInfo to get the version number
import packageInfo from '../../package.json';
import * as ServerTypes from '../../../server/src/types/index';
import { ExtractResponseBody as Body, DeepReplaceNonPrimitives, ExtractRequestParams as Params, ExtractResponseStatus as Status } from '../../../server/src/types/api';
import { getAuth } from 'firebase/auth';
import * as API from '../types/api';
import assert from 'node:assert';
import { ClassroomRatioTimeline } from '../../../server/src/types/classroom';
import { parse } from 'node:path';
import { isTourActiveGlobalGetter } from '../contexts/TourContext';

// Get API URL from environment variable or use default
const API_PATH_URL = '/api';
const API_URL =  import.meta.env.VITE_API_URL || ('http://localhost:5000' + API_PATH_URL);


// Custom Axios types to make the request and response types more specific
type CustomAxiosRequestConfig<Data, Params> = Omit<AxiosRequestConfig, 'data'|'params'> & { //stock AxiosRequestConfig doesn't type params
  data?: Data;
  params?: Params;
}
type CustomAxiosResponse<D, S extends number> = Omit<AxiosResponse, 'data' | 'status'> & {data: D;status: S;} //stock AxiosResponse doesn't expose status type to generic
type CustomAxiosInstance = Omit<Axios, 'get' | 'post'> & {
  get<Res = any, Req = any>(url: string, config?: CustomAxiosRequestConfig<never, Params<Req>>): Promise<CustomAxiosResponse<Body<Res>, Status<Res>>>;
  post<Res = any, Req = any>(url: string, data?: Params<Req>, config?: CustomAxiosRequestConfig<Params<Req>, any>): Promise<CustomAxiosResponse<Body<Res>, Status<Res>>>;
}

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'RAMON-CLIENT-VERSION': packageInfo.version
  },
}) as CustomAxiosInstance;

// Add request interceptor to automatically add Firebase token to each request
apiClient.interceptors.request.use(async (config) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    // If user is authenticated, get a fresh token and add it to headers
    if (user) {
      const token = await user.getIdToken(true);
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.error('Error getting authentication token:', error);
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Helper to generate a filename for the mock response based on URL and method
function getMockFilename(url: string, method: string): string {
  // Remove leading slash
  let cleanUrl = url.replace(/^\//, '');
  //add leading API_PATH_URL
  cleanUrl = API_PATH_URL + "/" + cleanUrl;
  // Replace slashes and special chars for filename safety
  cleanUrl = cleanUrl.replace(/[\/?&=]/g, '_').substring(0, 100);
  return `${method.toUpperCase()}_${cleanUrl}.json`;
}

// Axios request interceptor to rewrite URL for guided tour mock data
apiClient.interceptors.request.use((config) => {
  if (isTourActiveGlobalGetter && isTourActiveGlobalGetter()) {
    // Only rewrite if this is an API call (relative to /api)
    let url = config.url || '';
    // Remove baseURL if present
    if (url.startsWith(API_URL)) {
      url = url.slice(API_URL.length);
    }
    // Remove leading slash
    url = url.replace(/^\//, '');
    // Compose mock file path
    const method = (config.method || 'get').toUpperCase();
    const mockFilename = getMockFilename(url, method);
    config.baseURL = config.baseURL?.replace(/(\/|^)api(\/|$)/, '/');; // Prevent axios from prepending API_URL
    config.url = `/client_response_data/${mockFilename}`;
    // Optionally, force GET for static file
    config.method = 'get';
    // Remove params/body for static file fetch
    delete config.params;
    delete config.data;
  }
  return config;
}, (error) => Promise.reject(error));

// Track if refresh dialog is already shown to prevent multiple prompts
let refreshDialogShown = false;

// Version refresh - add response interceptor to check for refresh header
apiClient.interceptors.response.use(response => {
  // Check if server sent the RAMON-CLIENT-REFRESH header and a dialog isn't already shown
  if (response.headers && response.headers['ramon-client-refresh'] === 'true' && !refreshDialogShown) {
    // Set flag to prevent multiple dialogs
    refreshDialogShown = true;
    
    // Show refresh prompt to the user
    if (window.confirm('A new version of the application is available. Would you like to refresh now?')) {
      window.location.reload();
    } else {
      // Reset the flag after a short delay to prevent immediate re-prompting
      // but allow future prompts if user initially declines
      setTimeout(() => {
        refreshDialogShown = false;
      }, 5*60*1000); // 5 minutes (300,000 ms)
    }
  }
  return response;
});

// Enhanced helper function to extract error message
const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    // If server returned an error message, use it
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    // If there's a response with a status code but no error message
    if (error.response) {
      return `Server returned ${error.response.status} ${error.response.statusText || 'error'}`;
    }
    // If it's a network error
    if (error.message === 'Network Error') {
      return 'Unable to connect to server. Please check your connection.';
    }
    // Generic error with status code if available
    const axiosError = error as AxiosError;
    return `Request failed${axiosError.response ? ` (${axiosError.response.status})` : ''}: ${axiosError.message}`;
  }
  // For non-axios errors
  return error instanceof Error ? error.message : 'An unknown error occurred';
};

// Check response status and throw error if not OK
const checkResponse = (response: any): void => {
  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Request failed with status ${response.status}: ${response.statusText || 'Unknown error'}`);
  }
};

export class ApiService {
  

  // Get classroom ratios with all related data
  static async getClassroomRatios(): Promise<API.GetClassroomRatiosReturn> {
    try {
      const response = await apiClient.get<
        ServerTypes.API.GetRatiosResponse,
        ServerTypes.API.GetRatiosRequest
      >('/classrooms/ratios');
      checkResponse(response);
      return response.data;
    } catch (error) {
      console.error('Error fetching classroom ratios:', error);
      throw new Error(getErrorMessage(error));
    }
  }
  
  /**
   * Get classroom ratios over a specific time period
   * @param startTime - Start date for the time period (optional, defaults to 24 hours ago)
   * @param endTime - End date for the time period (optional, defaults to current time)
   * @returns Timeline of classroom ratios
   */
  static async getClassroomRatiosOverTime(
    startTime?: Date | string,
    endTime?: Date | string
  ): Promise<API.GetClassroomRatiosOverTimeReturn> { // Consider updating the return type with a proper interface
    try {
      // Format params
      const params:Params<ServerTypes.API.GetTimelineRequest> = {};
      
      if (startTime) {
        params.startTime = startTime instanceof Date 
          ? startTime.toISOString() 
          : startTime;
      }
      
      if (endTime) {
        params.endTime = endTime instanceof Date 
          ? endTime.toISOString() 
          : endTime;
      }
      
      const response = await apiClient.get<
        ServerTypes.API.GetTimelineResponse,
        ServerTypes.API.GetTimelineRequest
      >('/classrooms/ratios/timeline', { params });
      checkResponse(response);
      
      // Use JSON.parse with a reviver function to handle Infinity values
      // First convert the data to a string, then parse it with the custom reviver
      const serializedData = JSON.stringify(response.data);
      const parsedData = JSON.parse(serializedData, (key, value) => {
        if (value === "Infinity") return Infinity;
        if (value === "-Infinity") return -Infinity;
        return value;
      }) as DeepReplaceNonPrimitives<API.GetClassroomRatiosOverTimeReturn>;

      return {
        teachers: parsedData.teachers,
        students: parsedData.students,
        timeline: parsedData.timeline.map(classTimeline => {
          return {
            classId: classTimeline.classId,
            className: classTimeline.className,
            timeSegments: classTimeline.timeSegments.map(segment => {
              return {
                ...segment,
                startTime: new Date(segment.startTime),
                endTime: new Date(segment.endTime),
                studentIds: segment.studentIds,
                activeTeacherIds: segment.activeTeacherIds,
                teachersOnBreakIds: segment.teachersOnBreakIds
              }
            })
          }
        })
      }
    } catch (error) {
      console.error('Error fetching classroom ratios timeline:', error);
      throw new Error(getErrorMessage(error));
    }
  }
  
  // Update position to classroom mapping
  static async updatePositionClassroomMapping(mapping: Record<string, string[]>): Promise<void> {
    try {
      const response = await apiClient.post('/classrooms/position-mapping', mapping);
      checkResponse(response);
    } catch (error) {
      console.error('Error updating position-classroom mapping:', error);
      throw new Error(getErrorMessage(error));
    }
  }
  
  // Get teachers who are signed in SmartCare but not clocked in to WhenIWork
  //static async getNotClockedInTeachers(): Promise<NotClockedInTeacher[]> {
  //  try {
  //    const response = await apiClient.get<NotClockedInTeacher[]>('/smartcare/not-clocked-in-teachers');
  //    checkResponse(response);
  //    return response.data;
  //  } catch (error) {
  //    console.error('Error fetching not clocked in teachers:', error);
  //    throw new Error(getErrorMessage(error));
  //  }
  //}
  
  // Sign out a teacher from SmartCare
  static async logoutTeacher(teacherId: string): Promise<API.LogoutTeacherReturn> {
    try {
      const response = await apiClient.post<
        ServerTypes.API.PostLogoutTeacherResponse,
        ServerTypes.API.PostLogoutTeacherRequest
      >('/smartcare/logout-teacher', { teacherId });
      checkResponse(response);
      
      // Additional check for API success flag in response
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Operation failed');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error signing out teacher:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  // Get teachers who are signed in to SmartCare with their profiles
  static async getSignedInTeachersWithProfiles(): Promise<API.GetSignedInTeachersWithProfilesReturn> {
    try {
      const response = await apiClient.get<
        ServerTypes.API.GetSignedInEmployeesWithProfilesResponse,
        ServerTypes.API.GetSignedInEmployeesWithProfilesRequest
      >('/smartcare/signed-in-employees-with-profiles');
      checkResponse(response);
      return response.data;
    } catch (error) {
      console.error('Error fetching signed-in teachers with profiles:', error);
      throw new Error(getErrorMessage(error));
    }
  }
  
  // Get employees currently clocked in to WhenIWork
  static async getClockedInEmployees(): Promise<API.GetClockedInEmployeesReturn> {
    try {
      const response = await apiClient.get<
        ServerTypes.API.GetClockedInEmployeesResponse,
        ServerTypes.API.GetClockedInEmployeesRequest
      >('/wheniwork/clocked-in-employees');
      checkResponse(response);
      return response.data;
    } catch (error) {
      console.error('Error fetching clocked-in employees:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  // Get all teacher profiles from SmartCare
  static async getAllTeacherProfiles(): Promise<API.GetAllTeacherProfilesReturn> {
    try {
      const response = await apiClient.get<
        ServerTypes.API.GetAllTeacherProfilesResponse,
        ServerTypes.API.GetAllTeacherProfilesRequest
      >('/smartcare/all-teacher-profiles');
      checkResponse(response);
      return response.data;
    } catch (error) {
      console.error('Error fetching teacher profiles:', error);
      throw new Error(getErrorMessage(error));
    }
  }
  
  // Check in a teacher to SmartCare
  static async checkInTeacher(params: TeacherCheckInParams): Promise<API.CheckInTeacherReturn> {
    try {
      const response = await apiClient.post<
        ServerTypes.API.PostSignInToClassResponse,
        ServerTypes.API.PostSignInToClassRequest
      >('/smartcare/sign-in-to-class', params);
      checkResponse(response);
      
      // Additional check for API success flag in response
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Check-in operation failed');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error checking in teacher:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Move students to a different classroom in SmartCare
   */
  static async moveStudentsToClass(params: { childrenIds: string[], targetClassId: string }):
      Promise<API.MoveStudentsToClassReturn> {
    try {
      const response = await apiClient.post<
        ServerTypes.API.PostMoveChildrenToClassResponse,
        ServerTypes.API.PostMoveChildrenToClassRequest
      >('/smartcare/move-children-to-class', params);
      checkResponse(response);
      return response.data;
    } catch (error) {
      console.error('Error moving students to a different classroom:', error);
      throw new Error(getErrorMessage(error));
    }
  }
}

export default ApiService;